import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase } from '@material-ui/core';
import { adminClientsAPI, fareBaseAPI, fareBaseSaveAPI, fareClientAPI, fareClientSaveAPI, fareConfigAPI, fareConfigSaveAPI, fareLookupAPI } from '../../api/admin';
import { Card, Field, Input, Select, Toggle, Btn, Row, Grid, Badge, Empty, Msg, fmtWon } from './ui';
import tokens from '../theme/tokens';

// 운임 관리 — 퀴노스메인(FareEditor)과 같은 데이터·구조를 관리자 페이지에서.
//   ① 운임표: 일반운임(__base__)은 오토 + 화물 1t(vehicle '__cargo__'), 거래처운임은 차량별 열. 리스트 | 매트릭스(오토) 보기
//   ② 차량 요금 규칙: 금액대 구간(다마스 | 화물 1t × 서울↔서울·서울↔경인·경인↔경인), 톤수 규칙, 고정가, 차종 추가, 추가 옵션 요금
//      거래처는 '일반운임 따름 | 이 거래처 전용' 토글
//   ③ 운임 조회 테스트
const BASE = '__base__';
const CARGO = '__cargo__';
const CLIENT_VEHICLES = ['다마스', '라보', '밴', '트럭 1톤', '트럭 1.4톤', '트럭 2.5톤', '트럭 3.5톤', '트럭 5톤'];
const PAIRS = [['ss', '서울 ↔ 서울'], ['sg', '서울 ↔ 경인'], ['gg', '경인 ↔ 경인']];
const EXTRAS = [
  ['stair', '계단 수작업'], ['hand', '물품 수작업'], ['handCargo', '화물 수작업'], ['accompany', '동승'],
  ['waitAuto', '대기(오토) · 30분'], ['waitCar', '대기(다마스·라보) · 30분'], ['waitCargo', '대기(화물) · 30분'], ['keepNight', '보관 · 1박'],
];
const MATRIX_MAX = 30;
const emptyBands = () => ({ ss: [], sg: [], gg: [] });
const emptyConfig = () => ({
  mode: 'list', truckMode: 'list', vehicleRulesMode: 'inherit', adjustMode: 'add', adjusts: [], extras: {}, seoulBands: [],
  carBands: emptyBands(), truckBands: emptyBands(), truckTons: [], carFix: { damasSeoul: 25000, laboExtra: 5000, vanExtra: 5000, truckSeoul1t: 50000 }, bodyExtras: [], dongs: [], truckDongs: [],
});

const useStyles = makeStyles(() => ({
  owner: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 },
  chip: {
    height: 36, padding: '0 14px', borderRadius: 18, border: `1px solid ${tokens.line}`, backgroundColor: tokens.surface, fontSize: 13, fontWeight: 700, color: tokens.text,
    '&:hover': { borderColor: tokens.primary },
  },
  chipOn: { borderColor: tokens.primary, backgroundColor: tokens.primarySoft, color: tokens.primaryDark },
  tabs: { display: 'inline-flex', padding: 3, borderRadius: 10, backgroundColor: tokens.surfaceSoft },
  tab: { height: 30, padding: '0 12px', borderRadius: 8, fontSize: 13, fontWeight: 700, color: tokens.muted },
  tabOn: { backgroundColor: tokens.surface, color: tokens.ink, boxShadow: '0 1px 4px rgba(22,22,23,0.08)' },
  tableWrap: { width: '100%', overflowX: 'auto' },
  table: {
    borderCollapse: 'collapse', fontSize: 13, minWidth: '100%',
    '& th': { padding: '8px 6px', fontSize: 12, color: tokens.muted, textAlign: 'left', borderBottom: `1px solid ${tokens.lineSoft}`, whiteSpace: 'nowrap', position: 'sticky', top: 0, backgroundColor: tokens.surface },
    '& td': { padding: '6px 6px', borderBottom: `1px solid ${tokens.lineSoft}`, whiteSpace: 'nowrap' },
    '& input': { height: 34, fontSize: 13 },
  },
  cell: { width: 96, textAlign: 'right' },
  dong: { width: 120 },
  matrixWrap: { maxHeight: 520, overflow: 'auto' },
  matrixHead: { position: 'sticky', left: 0, backgroundColor: tokens.surface, fontWeight: 700, color: tokens.ink, zIndex: 1 },
  bandRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, alignItems: 'center', marginBottom: 6 },
  small: { fontSize: 12, color: tokens.muted },
}));

const toNum = (v) => { const n = Number(String(v == null ? '' : v).replace(/[^0-9.-]/g, '')); return Number.isFinite(n) ? n : 0; };
const pairKey = (a, b) => `${a}→${b}`;

function AdminFareManager() {
  const cs = useStyles();
  const [owner, setOwner] = useState(BASE);
  const [clients, setClients] = useState([]);
  const [rates, setRates] = useState([]);
  const [config, setConfig] = useState(emptyConfig());
  const [view, setView] = useState('list');
  const [bandTab, setBandTab] = useState('car');
  const [pairTab, setPairTab] = useState('ss');
  const [newRow, setNewRow] = useState({ startDong: '', destDong: '' });
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState({ ok: false, text: '' });
  const [busy, setBusy] = useState(false);
  const [lookup, setLookup] = useState({ vehicle: '', startDong: '', destDong: '', startSido: '서울', destSido: '서울', result: null });
  const isBase = owner === BASE;
  const columns = isBase ? [['', '오토'], [CARGO, '화물 1t']] : [['', '오토'], ...CLIENT_VEHICLES.map((v) => [v, v])];
  const rulesEditable = isBase || config.vehicleRulesMode === 'custom';

  const load = useCallback(async (own) => {
    setBusy(true); setMsg({ ok: false, text: '' });
    const [r, c] = await Promise.all([own === BASE ? fareBaseAPI() : fareClientAPI(own), fareConfigAPI(own)]);
    setRates(r && r.status === 200 ? (r.data.rates || []).map((x) => ({ vehicle: x.vehicle || '', startDong: x.startDong, destDong: x.destDong, amount: toNum(x.amount) })) : []);
    setConfig(c && c.status === 200 && c.data.config ? { ...emptyConfig(), ...c.data.config, carBands: { ...emptyBands(), ...(c.data.config.carBands || {}) }, truckBands: { ...emptyBands(), ...(c.data.config.truckBands || {}) }, carFix: { ...emptyConfig().carFix, ...(c.data.config.carFix || {}) } } : emptyConfig());
    setBusy(false);
  }, []);

  useEffect(() => {
    adminClientsAPI().then((resp) => { if (resp && resp.status === 200) setClients(resp.data.rows || []); });
    load(BASE);
  }, [load]);
  const pickOwner = (own) => { setOwner(own); setView('list'); load(own); };

  // 구간(출발→도착) 목록 — 같은 구간의 차량별 금액을 한 줄로
  const pairs = useMemo(() => {
    const map = new Map();
    rates.forEach((r) => { const k = pairKey(r.startDong, r.destDong); if (!map.has(k)) map.set(k, { startDong: r.startDong, destDong: r.destDong, amounts: {} }); map.get(k).amounts[r.vehicle] = r.amount; });
    const list = Array.from(map.values()).sort((a, b) => a.startDong.localeCompare(b.startDong) || a.destDong.localeCompare(b.destDong));
    const needle = q.trim();
    return needle ? list.filter((p) => p.startDong.includes(needle) || p.destDong.includes(needle)) : list;
  }, [rates, q]);
  const dongs = useMemo(() => {
    const set = new Set([...(config.dongs || []), ...rates.map((r) => r.startDong), ...rates.map((r) => r.destDong)]);
    return Array.from(set).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [rates, config.dongs]);

  const setAmount = (startDong, destDong, vehicle, value) => {
    const amount = toNum(value);
    setRates((prev) => {
      const idx = prev.findIndex((r) => r.startDong === startDong && r.destDong === destDong && r.vehicle === vehicle);
      if (idx < 0) return amount > 0 ? [...prev, { vehicle, startDong, destDong, amount }] : prev;
      const next = [...prev];
      if (amount > 0) next[idx] = { ...next[idx], amount }; else next.splice(idx, 1);
      return next;
    });
  };
  const amountOf = (startDong, destDong, vehicle) => {
    const hit = rates.find((r) => r.startDong === startDong && r.destDong === destDong && r.vehicle === vehicle)
      || rates.find((r) => r.startDong === destDong && r.destDong === startDong && r.vehicle === vehicle);
    return hit ? hit.amount : '';
  };
  const addPair = () => {
    const s = newRow.startDong.trim(), d = newRow.destDong.trim();
    if (!s || !d) return;
    if (!rates.some((r) => r.startDong === s && r.destDong === d)) setRates((prev) => [...prev, { vehicle: '', startDong: s, destDong: d, amount: 0 }]);
    setConfig((c) => ({ ...c, dongs: Array.from(new Set([...(c.dongs || []), s, d])) }));
    setNewRow({ startDong: '', destDong: '' });
  };
  const removePair = (s, d) => setRates((prev) => prev.filter((r) => !(r.startDong === s && r.destDong === d)));

  const saveRates = async () => {
    setBusy(true); setMsg({ ok: false, text: '' });
    const clean = rates.filter((r) => r.amount > 0).map((r) => ({ vehicle: r.vehicle, startDong: r.startDong, destDong: r.destDong, amount: r.amount }));
    const resp = isBase ? await fareBaseSaveAPI(clean) : await fareClientSaveAPI(owner, clean);
    setBusy(false);
    if (resp && resp.status === 200) setMsg({ ok: true, text: `운임표 ${resp.data.count != null ? resp.data.count + '건 ' : ''}저장했습니다.` });
    else setMsg({ ok: false, text: (resp && resp.data && resp.data.message) || '운임표를 저장하지 못했습니다.' });
  };
  const saveConfig = async () => {
    setBusy(true); setMsg({ ok: false, text: '' });
    const body = { ...config, dongs, truckDongs: dongs };
    const resp = await fareConfigSaveAPI(owner, body);
    setBusy(false);
    if (resp && resp.status === 200) setMsg({ ok: true, text: '차량 요금 규칙을 저장했습니다.' });
    else setMsg({ ok: false, text: (resp && resp.data && resp.data.message) || '규칙을 저장하지 못했습니다.' });
  };

  // 규칙 편집 도우미
  const bands = config[bandTab === 'car' ? 'carBands' : 'truckBands'][pairTab] || [];
  const setBands = (list) => setConfig((c) => ({ ...c, [bandTab === 'car' ? 'carBands' : 'truckBands']: { ...c[bandTab === 'car' ? 'carBands' : 'truckBands'], [pairTab]: list } }));
  const setBand = (i, k, v) => setBands(bands.map((b, idx) => (idx === i ? { ...b, [k]: toNum(v) } : b)));
  const setFix = (k) => (e) => setConfig((c) => ({ ...c, carFix: { ...c.carFix, [k]: toNum(e.target.value) } }));
  const setExtra = (k) => (e) => setConfig((c) => ({ ...c, extras: { ...c.extras, [k]: toNum(e.target.value) } }));
  const setTon = (i, k, v) => setConfig((c) => ({ ...c, truckTons: c.truckTons.map((t, idx) => (idx === i ? { ...t, [k]: k === 'value' ? toNum(v) : v } : t)) }));
  const setBody = (i, k, v) => setConfig((c) => ({ ...c, bodyExtras: c.bodyExtras.map((t, idx) => (idx === i ? { ...t, [k]: k === 'value' ? toNum(v) : v } : t)) }));

  const runLookup = async () => {
    const resp = await fareLookupAPI({ clientKey: isBase ? '' : owner, vehicle: lookup.vehicle, startDong: lookup.startDong, destDong: lookup.destDong, startSido: lookup.startSido, destSido: lookup.destSido });
    setLookup((l) => ({ ...l, result: resp && resp.status === 200 ? resp.data.fare : { error: (resp && resp.data && resp.data.message) || '조회 실패' } }));
  };

  const ownerName = isBase ? '일반운임' : ((clients.find((c) => c.clientKey === owner) || {}).clientName || owner);

  return (
    <React.Fragment>
      <Card title="운임 대상" desc="일반운임은 모든 고객의 기준, 거래처운임은 그 거래처와 연결된 기업회원에게 적용">
        <div className={cs.owner}>
          <ButtonBase className={clsx(cs.chip, isBase && cs.chipOn)} onClick={() => pickOwner(BASE)} focusRipple={false}>일반운임</ButtonBase>
          {clients.map((c) => (
            <ButtonBase key={c.clientKey} className={clsx(cs.chip, owner === c.clientKey && cs.chipOn)} onClick={() => pickOwner(c.clientKey)} focusRipple={false}>{c.clientName}</ButtonBase>
          ))}
        </div>
        <Msg ok={msg.ok}>{msg.text}</Msg>
      </Card>

      <Card title={`${ownerName} 운임표`} desc={isBase ? '오토 기본요금 + 화물 1t · 구간은 양방향으로 찾는다' : '차량별 명시 운임 · 비어 있으면 일반운임 규칙으로 계산'} action={(
        <Row>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="동 검색" style={{ width: 160, height: 36 }} />
          {isBase && (
            <span className={cs.tabs}>
              {[['list', '리스트'], ['matrix', '매트릭스']].map(([k, l]) => (
                <ButtonBase key={k} className={clsx(cs.tab, view === k && cs.tabOn)} onClick={() => setView(k)} focusRipple={false}>{l}</ButtonBase>
              ))}
            </span>
          )}
          <Btn onClick={saveRates} disabled={busy}>운임표 저장</Btn>
        </Row>
      )}>
        <Row style={{ marginBottom: 12 }}>
          <Input value={newRow.startDong} onChange={(e) => setNewRow((r) => ({ ...r, startDong: e.target.value }))} placeholder="출발 동" style={{ width: 150, height: 36 }} />
          <Input value={newRow.destDong} onChange={(e) => setNewRow((r) => ({ ...r, destDong: e.target.value }))} placeholder="도착 동" style={{ width: 150, height: 36 }} />
          <Btn tone="line" size="sm" onClick={addPair} disabled={!newRow.startDong.trim() || !newRow.destDong.trim()}>구간 추가</Btn>
          <span className={cs.small}>{pairs.length}구간 · {rates.filter((r) => r.amount > 0).length}건</span>
        </Row>
        {view === 'matrix' && isBase ? (
          dongs.length > MATRIX_MAX ? <Empty>동이 {dongs.length}개라 매트릭스 대신 리스트로 편집하세요.</Empty> : (
            <div className={clsx(cs.tableWrap, cs.matrixWrap)}>
              <table className={cs.table}>
                <thead><tr><th className={cs.matrixHead}>오토 (출발 ↓ / 도착 →)</th>{dongs.map((d) => <th key={d}>{d}</th>)}</tr></thead>
                <tbody>
                  {dongs.map((s) => (
                    <tr key={s}>
                      <td className={cs.matrixHead}>{s}</td>
                      {dongs.map((d) => (
                        <td key={d}><Input className={cs.cell} value={amountOf(s, d, '')} onChange={(e) => setAmount(s, d, '', e.target.value)} placeholder="-" inputMode="numeric" /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          !pairs.length ? <Empty>구간이 없습니다. 위에서 출발 동·도착 동을 추가하세요.</Empty> : (
            <div className={cs.tableWrap}>
              <table className={cs.table}>
                <thead><tr><th>출발 동</th><th>도착 동</th>{columns.map(([k, l]) => <th key={k}>{l}</th>)}<th /></tr></thead>
                <tbody>
                  {pairs.map((p) => (
                    <tr key={pairKey(p.startDong, p.destDong)}>
                      <td className={cs.dong}>{p.startDong}</td>
                      <td className={cs.dong}>{p.destDong}</td>
                      {columns.map(([k]) => (
                        <td key={k}><Input className={cs.cell} value={p.amounts[k] || ''} onChange={(e) => setAmount(p.startDong, p.destDong, k, e.target.value)} placeholder="-" inputMode="numeric" /></td>
                      ))}
                      <td><Btn tone="danger" size="sm" onClick={() => removePair(p.startDong, p.destDong)}>삭제</Btn></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </Card>

      <Card title="차량 요금 규칙" desc={isBase ? '오토 금액을 기준으로 다마스·라보·밴·트럭 요금을 만든다' : '일반운임 규칙을 따르거나 이 거래처만의 규칙을 둔다'} action={(
        <Row>
          {!isBase && <Toggle checked={config.vehicleRulesMode === 'custom'} onChange={(v) => setConfig((c) => ({ ...c, vehicleRulesMode: v ? 'custom' : 'inherit' }))} label={config.vehicleRulesMode === 'custom' ? '이 거래처 전용' : '일반운임 따름'} />}
          <Btn onClick={saveConfig} disabled={busy}>규칙 저장</Btn>
        </Row>
      )}>
        {!rulesEditable ? <Empty>일반운임의 규칙을 그대로 씁니다.</Empty> : (
          <React.Fragment>
            <Grid cols={3}>
              <Field label="다마스 서울↔서울 고정가"><Input type="number" value={config.carFix.damasSeoul} onChange={setFix('damasSeoul')} /></Field>
              <Field label="라보 = 다마스 +"><Input type="number" value={config.carFix.laboExtra} onChange={setFix('laboExtra')} /></Field>
              <Field label="밴 = 다마스 +"><Input type="number" value={config.carFix.vanExtra} onChange={setFix('vanExtra')} /></Field>
            </Grid>
            <Grid cols={3}>
              <Field label="트럭 1t 서울↔서울 고정가"><Input type="number" value={config.carFix.truckSeoul1t} onChange={setFix('truckSeoul1t')} /></Field>
            </Grid>

            <Row style={{ margin: '6px 0 10px' }}>
              <span className={cs.tabs}>
                {[['car', '다마스 금액대'], ['truck', '화물 1t 금액대']].map(([k, l]) => <ButtonBase key={k} className={clsx(cs.tab, bandTab === k && cs.tabOn)} onClick={() => setBandTab(k)} focusRipple={false}>{l}</ButtonBase>)}
              </span>
              <span className={cs.tabs}>
                {PAIRS.map(([k, l]) => <ButtonBase key={k} className={clsx(cs.tab, pairTab === k && cs.tabOn)} onClick={() => setPairTab(k)} focusRipple={false}>{l}</ButtonBase>)}
              </span>
              <Btn tone="line" size="sm" onClick={() => setBands([...bands, { from: 0, to: 0, amount: 0 }])}>구간 추가</Btn>
            </Row>
            <div className={cs.bandRow}><span className={cs.small}>{bandTab === 'car' ? '오토 금액' : '1t 기준 금액'} 이상</span><span className={cs.small}>미만 (0 = 끝)</span><span className={cs.small}>{bandTab === 'car' ? '다마스 요금' : '트럭 1t 요금'}</span><span /></div>
            {!bands.length && <Empty>구간이 없으면 고정가(서울↔서울) 또는 오토 금액을 그대로 씁니다.</Empty>}
            {bands.map((b, i) => (
              <div key={i} className={cs.bandRow}>
                <Input type="number" value={b.from} onChange={(e) => setBand(i, 'from', e.target.value)} />
                <Input type="number" value={b.to} onChange={(e) => setBand(i, 'to', e.target.value)} />
                <Input type="number" value={b.amount} onChange={(e) => setBand(i, 'amount', e.target.value)} />
                <Btn tone="danger" size="sm" onClick={() => setBands(bands.filter((_, idx) => idx !== i))}>삭제</Btn>
              </div>
            ))}

            <Row style={{ margin: '18px 0 8px' }}><b style={{ fontSize: 14 }}>톤수 규칙 (1t 기준)</b><Btn tone="line" size="sm" onClick={() => setConfig((c) => ({ ...c, truckTons: [...c.truckTons, { ton: '', mode: 'add', value: 0 }] }))}>추가</Btn></Row>
            {!config.truckTons.length && <div className={cs.small}>비어 있으면 1.4t +20,000원 · 2.5t ×2 기본 규칙을 쓴다.</div>}
            {config.truckTons.map((t, i) => (
              <div key={i} className={cs.bandRow}>
                <Input value={t.ton} onChange={(e) => setTon(i, 'ton', e.target.value)} placeholder="톤수 (1.4, 2.5, 3.5, 5)" />
                <Select value={t.mode} onChange={(e) => setTon(i, 'mode', e.target.value)}><option value="add">+ 금액</option><option value="mult">× 배율</option></Select>
                <Input type="number" value={t.value} onChange={(e) => setTon(i, 'value', e.target.value)} />
                <Btn tone="danger" size="sm" onClick={() => setConfig((c) => ({ ...c, truckTons: c.truckTons.filter((_, idx) => idx !== i) }))}>삭제</Btn>
              </div>
            ))}

            <Row style={{ margin: '18px 0 8px' }}><b style={{ fontSize: 14 }}>차종 추가 요금 (윙바디·냉동탑 등)</b><Btn tone="line" size="sm" onClick={() => setConfig((c) => ({ ...c, bodyExtras: [...c.bodyExtras, { body: '', value: 0 }] }))}>추가</Btn></Row>
            {config.bodyExtras.map((b, i) => (
              <div key={i} className={cs.bandRow}>
                <Input value={b.body} onChange={(e) => setBody(i, 'body', e.target.value)} placeholder="차종 (윙바디)" />
                <Input type="number" value={b.value} onChange={(e) => setBody(i, 'value', e.target.value)} />
                <span />
                <Btn tone="danger" size="sm" onClick={() => setConfig((c) => ({ ...c, bodyExtras: c.bodyExtras.filter((_, idx) => idx !== i) }))}>삭제</Btn>
              </div>
            ))}

            <Row style={{ margin: '18px 0 8px' }}><b style={{ fontSize: 14 }}>추가 옵션 요금</b></Row>
            <Grid cols={3}>
              {EXTRAS.map(([k, l]) => <Field key={k} label={l}><Input type="number" value={config.extras[k] || ''} onChange={setExtra(k)} placeholder="0" /></Field>)}
            </Grid>
          </React.Fragment>
        )}
      </Card>

      <Card title="운임 조회 테스트" desc={`${ownerName} 기준으로 계산`}>
        <Grid cols={3}>
          <Field label="차량">
            <Select value={lookup.vehicle} onChange={(e) => setLookup((l) => ({ ...l, vehicle: e.target.value }))}>
              <option value="">오토</option>
              {CLIENT_VEHICLES.map((v) => <option key={v} value={v}>{v}</option>)}
            </Select>
          </Field>
          <Field label="출발 동 · 시도"><Row><Input value={lookup.startDong} onChange={(e) => setLookup((l) => ({ ...l, startDong: e.target.value }))} placeholder="역삼동" /><Input value={lookup.startSido} onChange={(e) => setLookup((l) => ({ ...l, startSido: e.target.value }))} style={{ width: 80 }} /></Row></Field>
          <Field label="도착 동 · 시도"><Row><Input value={lookup.destDong} onChange={(e) => setLookup((l) => ({ ...l, destDong: e.target.value }))} placeholder="순화동" /><Input value={lookup.destSido} onChange={(e) => setLookup((l) => ({ ...l, destSido: e.target.value }))} style={{ width: 80 }} /></Row></Field>
        </Grid>
        <Row>
          <Btn onClick={runLookup} disabled={!lookup.startDong || !lookup.destDong}>조회</Btn>
          {lookup.result && !lookup.result.error && (
            <Row><b style={{ fontSize: 18, color: tokens.primaryDark }}>{fmtWon(lookup.result.amount)}</b><Badge tone={lookup.result.source === 'base_default' ? 'off' : 'on'}>{lookup.result.source}{lookup.result.via ? ` · ${lookup.result.via}` : ''}{lookup.result.pair ? ` · ${lookup.result.pair}` : ''}</Badge></Row>
          )}
          {lookup.result && lookup.result.error && <Msg>{lookup.result.error}</Msg>}
        </Row>
      </Card>
    </React.Fragment>
  );
}

export default AdminFareManager;
